"use client";

import React, { JSX, useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Icon from "../icons/Icons";

interface CustomToastProps {
  id?: number;
  title: string;
  description: string;
  status: "success" | "failed" | "pending" | "normal";
  duration?: number;
}

const statusStyles: Record<string, string> = {
  success: "bg-[#D1F4E0] text-[#147D41]",
  failed: "bg-[#FDD0DF] text-[#CC3066]",
  pending: "bg-[#FDEDD3] text-[#95661B]",
  normal: "bg-[#EEEEEF] text-[#616167]",
};

const statusIcons: Record<CustomToastProps["status"], JSX.Element> = {
  success: <Icon.toastSuccessIcon />,
  failed: <Icon.toastFailedIcon />,
  pending: <Icon.toastPendingIcon />,
  normal: <Icon.toastDefaultIcon />,
};

type Listener = (toast: CustomToastProps) => void;
let listeners: Listener[] = [];

export function addToast(toast: Omit<CustomToastProps, "id">) {
  const id = Date.now() + Math.random();
  const toastWithId: CustomToastProps = { id, ...toast };
  listeners.forEach((l) => l(toastWithId));
}

export default function CustomToastManager() {
  const [toasts, setToasts] = useState<CustomToastProps[]>([]);

  useEffect(() => {
    function onNewToast(toast: CustomToastProps) {
      setToasts((prev) => [toast, ...prev]); // add at top
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
      }, toast.duration ?? 4000);
    }
    listeners.push(onNewToast);
    return () => {
      listeners = listeners.filter((l) => l !== onNewToast);
    };
  }, []);

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <div className="fixed bottom-6 right-6 flex flex-col-reverse gap-2 z-9999 max-h-56 overflow-hidden">
      <AnimatePresence initial={false}>
        {toasts.map((toast) => (
          <motion.div
            key={toast.id}
            layout // <-- enables smooth repositioning
            initial={{ opacity: 0, x: 50, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 50, scale: 0.9 }}
            transition={{ duration: 0.25 }}
            className={`relative rounded-lg shadow-md p-4 flex gap-3 items-center min-w-200 ${statusStyles[toast.status]} group`}
          >
            <div className="shrink-0">{statusIcons[toast.status]}</div>
            <div className="flex flex-col">
              <div className="font-semibold text-sm">{toast.title}</div>
              <div className="text-xs opacity-80">{toast.description}</div>
            </div>
            <button onClick={() => removeToast(toast.id!)} className="opacity-0 group-hover:opacity-100 text-xs absolute top-1 right-1 transition-opacity">
              ✕
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// This is How this toast used
{
  /* <button
  onClick={() =>
    addToast({
      title: "Success",
      description: "Your request completed successfully.",
      status: "failed",
      duration: 40000,
    })
  }
  className="btn btn-primary"
>
  Show Toast
</button> */
}
